import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { AuthProvider } from './contexts/auth-context';
import { queryClient } from './lib/query-client';
import { setupThemeListener } from './stores/theme-store';
import { ToastContainer } from './components/ToastContainer';
import { Toaster } from 'react-hot-toast';
import { PageTitleManager } from './components/PageTitleManager';
import { ErrorBoundary } from './components/ErrorBoundary';
import { LanguagePrefetch } from './components/LanguagePrefetch';
import { OfflineIndicator } from './components/OfflineIndicator';
import { NProgressBar } from './components/nprogress-bar';
import { useAuthStore } from './stores/auth-store';
import Routes from './routes';

const AppContent: React.FC = () => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const token = useAuthStore((state) => state.token);
  const user = useAuthStore((state) => state.user);
  const fetchProfile = useAuthStore((state) => state.fetchProfile);
  const [isProfileReady, setIsProfileReady] = useState(false);

  useEffect(() => {
    // Only fetch profile if authenticated, has token, but no user data yet
    if (isAuthenticated && token && !user) {
      fetchProfile().then(() => {
        setIsProfileReady(true);
      }).catch(() => {
        setIsProfileReady(true);
      });
    } else {
      setIsProfileReady(true);
    }
  }, [isAuthenticated, token, user]);

  return (
    <>
      <NProgressBar />
      {isAuthenticated && isProfileReady && <LanguagePrefetch />}
      <Routes />
    </>
  );
};

function App() {
  useEffect(() => {
    const cleanup = setupThemeListener();
    return cleanup;
  }, []);

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <Router>
          <PageTitleManager />
          <OfflineIndicator />
          <AuthProvider>
            <AppContent />
          </AuthProvider>
        </Router>
        <ToastContainer />
        <Toaster
          position="top-center"
          reverseOrder={false}
          containerStyle={{ zIndex: 99999 }}
          toastOptions={{
            duration: 4000,
            style: {
              background: '#1e293b',
              color: '#fff',
              fontSize: '14px',
              borderRadius: '12px',
              padding: '12px 18px',
              boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.2)',
            },
            success: {
              style: {
                background: '#059669',
                color: '#fff',
              },
              iconTheme: {
                primary: '#fff',
                secondary: '#059669',
              },
            },
            error: {
              style: {
                background: '#dc2626',
                color: '#fff',
              },
              iconTheme: {
                primary: '#fff',
                secondary: '#dc2626',
              },
            },
          }}
        />
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
